/**
 * Sending a walk-in lot (C2b): the half of "Add a lot at the ring" that talks to
 * the API, kept out of main.ts so it can be tested without the whole console.
 *
 * lot-dialog.ts owns the form; this owns what happens after Add: one createLot
 * round trip (ADR-29 confirm: true, the dialog being the review), the toast and
 * the feed line, a catalog refresh, and loading the lot when asked.
 *
 * The one rule that matters on a slow ring link: a failure that is not a clean
 * 4xx may have landed AFTER the write. So on a taken number, a server error or
 * no answer at all this refreshes the catalog before the dialog decides what to
 * say (it looks for the clerk's own lot there), and it never tells the clerk the
 * lot was not added when it cannot know.
 */

import { ApiError } from '@hipnumber/api-contract/client';
import { addLotFailure, type NewLotBody, type SubmitResult } from './lot-dialog.js';
import type { LotWords } from './lot-term.js';

/** What createLot is sent: the dialog's body, the ring when the sale has several, and the ADR-29 confirm. */
export type CreateLotBody = NewLotBody & { ring?: string; confirm: true };

export interface WalkInIo {
  /** POST createLot for this sale. Resolves with the created lot; rejects with ApiError, or any Error when no usable answer came. */
  createLot: (body: CreateLotBody) => Promise<{ id: string; hipNumber: string }>;
  refreshCatalog: () => Promise<unknown>;
  say: (text: string, tone: 'ok' | 'err') => void;
  pushFeed: (line: string) => void;
  setLot: (lotId: string) => void;
  words: () => LotWords;
}

const REASON_MAX = 120;

const reasonOf = (e: unknown): string => {
  const text = e instanceof Error ? e.message : String(e);
  return text.length > REASON_MAX ? `${text.slice(0, REASON_MAX)}...` : text;
};

/**
 * @param ring the engine ring id for a multi-ring sale (a lot added at a ring's
 *   console lands in that ring, cut 5c), else undefined.
 */
export async function addWalkInLot(io: WalkInIo, lotBody: NewLotBody, loadNow: boolean, ring?: string): Promise<SubmitResult> {
  const words = io.words();
  let created: { id: string; hipNumber: string };
  try {
    created = await io.createLot({ ...lotBody, ...(ring !== undefined ? { ring } : {}), confirm: true });
  } catch (e) {
    // Catch the catalog up when the lot may exist or the number is taken: a 409
    // (another clerk, or our own lot behind a lost reply), a 5xx (a gateway can
    // answer after the write) and no answer at all. A 4xx that says "no" (422,
    // 403) wrote nothing, so there is nothing to look for.
    const status = e instanceof ApiError ? e.status : null;
    const mayHaveLanded = status === null || status >= 500;
    if (mayHaveLanded || status === 409) await io.refreshCatalog().catch(() => undefined);
    const failure = e instanceof ApiError
      ? addLotFailure(e.status, e.body, words)
      : `Could not confirm the ${words.low} was added (${reasonOf(e)}). Check the connection, then look in the catalog before adding again.`;
    // "failed" only where the API said no. A taken number, a server error and
    // silence can each be the clerk's own lot, so the toast does not claim a
    // failure the dialog (which can see the refreshed catalog) may overturn.
    io.say(`${mayHaveLanded || status === 409 ? 'add lot not confirmed' : 'add lot failed'}: ${failure}`, 'err');
    return mayHaveLanded ? { ok: false, error: failure, unconfirmed: true } : { ok: false, error: failure };
  }
  io.say(`added ${words.low} ${created.hipNumber}`, 'ok');
  io.pushFeed(`＋ added ${words.low} ${created.hipNumber} - ${lotBody.title}`);
  // The lot exists whatever happens next; a catalog that will not refresh must
  // not read as "the add failed" and invite a second, duplicate add.
  try {
    await io.refreshCatalog();
  } catch {
    io.say(`added ${words.low} ${created.hipNumber}, but the catalog did not refresh - reload the page`, 'err');
  }
  if (loadNow) io.setLot(created.id);
  return { ok: true };
}
